import React, { useMemo, useState } from "react";
import { Clock, Users } from "lucide-react";

const toMin = (t) => { const [h, m] = (t || "0:0").split(":").map(Number); return h * 60 + m; };
const fmt = (min) => `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
export const nowRounded = (step = 15) => { const d = new Date(); const m = Math.round((d.getHours() * 60 + d.getMinutes()) / step) * step; return fmt(Math.min(m, 23 * 60 + 45)); };

// TheFork-style quick time picker: one tap on a slot, grouped by service, with covers already booked per slot.
export function TimeSlotPicker({ value, onChange, services = {}, slotMinutes = 15, rows = [], capacity, open: openProp, testId = "slot-picker" }) {
  const [open, setOpen] = useState(!!openProp);
  const [custom, setCustom] = useState(false);
  const groups = useMemo(() => Object.entries(services).map(([key, s]) => {
    const slots = [];
    for (let m = toMin(s.start); m <= toMin(s.end); m += slotMinutes) slots.push(fmt(m));
    const covers = rows.filter((r) => r.service === key && r.type === "table" && !["cancelled", "no_show"].includes(r.status));
    const perSlot = Object.fromEntries(slots.map((t) => [t, covers.filter((r) => r.time === t).reduce((a, r) => a + Number(r.party_size || 0), 0)]));
    return { key, label: s.label, slots, perSlot, total: covers.reduce((a, r) => a + Number(r.party_size || 0), 0) };
  }), [services, slotMinutes, rows]);
  const inGrid = groups.some((g) => g.slots.includes(value));
  const pick = (t) => { onChange(t); setOpen(false); setCustom(false); };

  return (
    <div className="space-y-2" data-testid={testId}>
      <button type="button" onClick={() => setOpen((o) => !o)} className={`w-full h-12 rounded-2xl border bg-white px-4 text-base font-bold inline-flex items-center justify-between ${open ? "border-[#C48B31] ring-2 ring-[#C48B31]/30" : "border-[#e8dcc8]"}`} data-testid={`${testId}-toggle`}>
        <span className="inline-flex items-center gap-2"><Clock className="h-4 w-4 text-[#C48B31]" /> {value || "Orario"}</span>
        <span className="text-xs font-semibold text-[#7c5a4e]">{open ? "chiudi" : "cambia"}</span>
      </button>
      {open && (
        <div className="rounded-2xl border border-[#e8dcc8] bg-white p-3 space-y-3 max-h-[46vh] overflow-y-auto" data-testid={`${testId}-grid`}>
          <div className="flex gap-2">
            <button type="button" onClick={() => pick(nowRounded(slotMinutes))} className="flex-1 h-9 rounded-full bg-[#faf3ee] text-xs font-bold text-[#4a1c17]" data-testid={`${testId}-now`}>Adesso · {nowRounded(slotMinutes)}</button>
            <button type="button" onClick={() => setCustom((c) => !c)} className={`flex-1 h-9 rounded-full text-xs font-bold ${custom || (!inGrid && value) ? "bg-[#4a1c17] text-white" : "bg-[#faf3ee] text-[#4a1c17]"}`} data-testid={`${testId}-custom`}>Altro orario</button>
          </div>
          {custom && <input type="time" value={value} onChange={(e) => e.target.value && onChange(e.target.value)} className="w-full h-11 rounded-2xl border border-[#e8dcc8] px-3 text-base" data-testid={`${testId}-custom-input`} />}
          {groups.map((g) => (
            <div key={g.key}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="font-bold text-sm text-[#2b1410]">{g.label}</span>
                <span className="rounded-full bg-[#faf3ee] px-2 py-0.5 text-[11px] font-semibold text-[#7c5a4e]">{g.total}{capacity ? `/${capacity}` : ""}p</span>
              </div>
              <div className="grid grid-cols-4 gap-1.5">
                {g.slots.map((t) => {
                  const n = g.perSlot[t] || 0;
                  const sel = t === value;
                  return (
                    <button key={t} type="button" onClick={() => pick(t)} data-testid={`${testId}-slot-${t}`}
                      className={`h-12 rounded-xl border text-sm font-bold flex flex-col items-center justify-center leading-tight transition-colors ${sel ? "bg-[#4a1c17] text-white border-[#4a1c17]" : n ? "bg-[#fdf6ea] border-[#e8dcc8] text-[#2b1410]" : "bg-white border-[#e8dcc8] text-[#2b1410] hover:bg-[#faf3ee]"}`}>
                      {t}
                      <span className={`text-[10px] font-semibold inline-flex items-center gap-0.5 ${sel ? "text-white/80" : n ? "text-[#C48B31]" : "text-[#c9b8a8]"}`}><Users className="h-2.5 w-2.5" /> {n}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
